/**
 * Normalizes a date string to YYYY-MM-DD format without timezone conversion
 * @param dateString A date string in any format
 * @returns A date string in YYYY-MM-DD format
 */
export function normalizeDate(dateString: string | null | undefined): string | null {
  if (!dateString) return null;
  
  // Parse the date in local timezone
  const date = new Date(dateString);
  
  // Format as YYYY-MM-DD (date only, no time component)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Creates a Date object at noon to avoid timezone issues
 * @param dateString A date string in any format
 * @returns A Date object set to noon
 */
export function createDateAtNoon(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  
  const normalizedDate = normalizeDate(dateString);
  if (!normalizedDate) return null;
  
  const [year, month, day] = normalizedDate.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0);
}

/**
 * Formats a date range as a string, ensuring dates are normalized
 * @param startDate The start date string
 * @param endDate The end date string
 * @returns A formatted date range string
 */
export function formatDateRange(startDate: string | null, endDate: string | null): string {
  if (!startDate || !endDate) return 'Not set';
  
  // Normalize dates to prevent timezone issues
  const normalizedStart = normalizeDate(startDate);
  const normalizedEnd = normalizeDate(endDate);
  
  if (!normalizedStart || !normalizedEnd) return 'Not set';
  
  // Format dates for display (e.g., "Apr 15 - May 30")
  const start = new Date(normalizedStart);
  const end = new Date(normalizedEnd);
  
  const startMonth = start.toLocaleString('default', { month: 'short' });
  const endMonth = end.toLocaleString('default', { month: 'short' });
  
  return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}`;
}

/**
 * Gets the current date at noon in YYYY-MM-DD format
 * @returns Current date string in YYYY-MM-DD format
 */
export function getTodayNormalized(): string {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
}

/**
 * Calculates the position of today in a timeline
 * @param viewStart The start date of the timeline view
 * @param timelineDuration The duration of the timeline in milliseconds
 * @returns CSS position object or null if today is outside the timeline
 */
export function getTodayPosition(viewStart: Date, timelineDuration: number): { left: string } | null {
  const today = new Date();
  const monthEnd = new Date(viewStart);
  monthEnd.setMonth(monthEnd.getMonth() + 1, 0);
  
  if (today < viewStart || today > monthEnd) return null;

  return {
    left: `${((today.getTime() - viewStart.getTime()) / timelineDuration) * 100}%`
  };
}

/**
 * Determines if a plant is frost sensitive based on its frost tolerance value
 * @param frostTolerance The frost tolerance string from the plant record
 * @returns Boolean indicating if the plant is frost sensitive
 */
export function isFrostSensitive(frostTolerance: string | null | undefined): boolean {
  if (!frostTolerance) return false;
  
  const normalizedTolerance = frostTolerance.toLowerCase().trim();
  return normalizedTolerance === 'frost sensitive';
}

/**
 * Determines if a plant is cold hardy based on its frost tolerance value
 * @param frostTolerance The frost tolerance string from the plant record
 * @returns Boolean indicating if the plant is cold hardy
 */
export function isColdHardy(frostTolerance: string | null | undefined): boolean {
  if (!frostTolerance) return false;
  
  const normalizedTolerance = frostTolerance.toLowerCase().trim();
  return ['cold hardy', 'frost hardy', 'cold tolerant', 'frost tolerant'].includes(normalizedTolerance);
}

/**
 * Calculates the number of days between two dates
 * @param startDate The start date string
 * @param endDate The end date string
 * @returns Number of days between dates or null if dates are invalid
 */
export function getDaysBetweenDates(startDate: string | null, endDate: string | null): number | null {
  if (!startDate || !endDate) return null;
  
  const start = createDateAtNoon(startDate);
  const end = createDateAtNoon(endDate);
  
  if (!start || !end) return null;
  
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}